import React from 'react';
import PoLink from '../PoDetailModal/PoLink';
import { fmtMoney } from './accountsFormat';

/*
 * One renewal row, shared by the Renewals widget and the Procurement tab.
 *
 * Extracted rather than copied: the widget shows a window onto this list and
 * the tab shows all of it, but a renewal has to look and behave the same in
 * both or the "open the full view" arrow lands somewhere that reads as a
 * different feature.
 *
 * The row is a TARGET. It used to be a plain <div> — no onClick, no href, no
 * keyboard affordance — which is most of what "the renewals widget does not
 * drill down into anything" meant. Every row from /api/v1/renewals carries
 * `poNumbers` (the endpoint aggregates them per contract), so the destination
 * already existed; nothing was wired to it.
 */


function fmtDay(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Competitor security paper is a displacement window; ours is a defence.
 *  Same sort GAS uses: ours last, then soonest expiry. */
export function sortRenewals(rows) {
  return [...rows].sort((a, b) => {
    if (!!a.ours !== !!b.ours) return a.ours ? 1 : -1;
    return (a.daysUntil ?? 1e9) - (b.daysUntil ?? 1e9);
  });
}

export function RenewalRow({ rn, departmentId }) {
  const urgencyClass = rn.urgency === 'urgent'
    ? ' accounts-due-urgent'
    : rn.urgency === 'soon' ? ' accounts-due-soon' : '';

  const poNumbers = Array.isArray(rn.poNumbers) ? rn.poNumbers.filter(Boolean) : [];
  const primaryPo = poNumbers[0] || null;

  const body = (
    <>
      <span className="accounts-renewal-head">
        <span className="accounts-renewal-vendor">{rn.vendor}</span>
        {rn.ours ? <span className="accounts-chip accounts-chip-ours">ours</span> : null}
        <span className="accounts-renewal-value">
          {rn.contractValueFormatted || fmtMoney(rn.contractValue)}
        </span>
      </span>
      <span className="accounts-renewal-meta">
        {rn.product && <span className="accounts-renewal-product">{rn.product}</span>}
        {poNumbers.length > 1 && (
          <span className="accounts-renewal-pos">{poNumbers.length} POs</span>
        )}
        <span className={`accounts-renewal-due${urgencyClass}`}>
          {fmtDay(rn.endDate)}
          {rn.daysUntil != null && ` · ${rn.daysUntil}d`}
        </span>
      </span>
    </>
  );

  // A row with no PO behind it is not a dead end being hidden — it is a
  // contract the matview has no document number for. Render it plainly rather
  // than as a control that does nothing when clicked.
  if (!primaryPo) {
    return <div className="accounts-renewal">{body}</div>;
  }

  return (
    <PoLink
      documentNumber={primaryPo}
      departmentId={departmentId}
      className="accounts-renewal accounts-renewal--link"
      title={`Open PO ${primaryPo} — ${rn.vendor}`}
    >
      {body}
    </PoLink>
  );
}

export default RenewalRow;
